import { useCallback, useEffect, useState } from "react";
import CampaignsMenu from "../../components/campaignsMenu/CampaignsMenu";
import Loading from "../../components/loading/Loading";
import Pagination from "../../components/pagination/Pagination";
import { useTranslator } from "../../context/translator";
import { Link, useParams } from "react-router-dom";
import {
  getCampaignCustomersByCampaignListIdService,
  deleteCampaignCustomerByIdService,
} from "services/campaignCustomer/services";
import { getCampaignListByIdService } from "services/campaignList/services";
import ConfirmDeleteModal from "components/confirmDeleteModal/ConfirmDeleteModal";
import CampaignCustomerAddModal from "components/campaignCustomerAddModal/CampaignCustomerAddModal";
import { useNotifications } from "context/Notifications";

const EmptyState = () => (
  <tr>
    <td colSpan={7}>
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <div className="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-4">
          <svg
            width="32"
            height="32"
            fill="none"
            viewBox="0 0 24 24"
            stroke="#1a56db"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m4-4a4 4 0 110-8 4 4 0 010 8zm6 4a2 2 0 100-4 2 2 0 000 4zM3 18a2 2 0 100-4 2 2 0 000 4z"
            />
          </svg>
        </div>
        <p className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-1">
          No lists yet
        </p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mb-6 max-w-xs">
          Create your first customer list to start segmenting your campaigns.
        </p>
      </div>
    </td>
  </tr>
);

const CampaignEditCustomersList = () => {
  const { listId } = useParams();
  const { addNotification } = useNotifications();

  const [customersData, setCustomersData] = useState([]);
  const [campaignListData, setCampaignListData] = useState(null);
  const [page, setPage] = useState(1);
  const [pageNumber, setPageNumber] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCustomerLists, setTotalCustomerLists] = useState(0);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const { translate } = useTranslator();
  const [search, setSearch] = useState("");

  const handleRemoveCustomer = async () => {
    if (!selectedFile) return;

    setIsLoading(true);
    try {
      const response = await deleteCampaignCustomerByIdService({
        campaignCustomerId: selectedFile._id,
      });
      if (response?.campaignCustomer?.success) {
        addNotification({
          id: Date.now(),
          message: "Customer removed from List!",
          kind: "success",
        });
        getCustomers();
      } else {
        addNotification({
          id: Date.now(),
          message:
            response?.campaignCustomer?.message ||
            "Error removing customer from list!",
          kind: "danger",
        });
      }
    } finally {
      setIsLoading(false);
      setIsDeleteModalOpen(false);
      setSelectedFile(null);
    }
  };

  const getCampaignList = useCallback(async () => {
    if (!listId) return;
    setIsLoading(true);
    try {
      const data = await getCampaignListByIdService({
        campaignListId: listId,
      });
      setCampaignListData(data?.campaignList ?? null);
    } finally {
      setIsLoading(false);
    }
  }, [listId]);

  useEffect(() => {
    getCampaignList();
  }, [getCampaignList]);

  const getCustomers = useCallback(async () => {
    if (!listId) return;
    setIsLoading(true);
    try {
      const data = await getCampaignCustomersByCampaignListIdService({
        page,
        search,
        campaignListId: listId,
        pageSize: 5,
      });
      setCustomersData(data?.customers?.customers ?? []);
      const count = data?.customers?.totalRecords ?? 0;
      setTotalCustomerLists(count);
      setPageNumber(Math.ceil(count / 5));
    } finally {
      setIsLoading(false);
    }
  }, [listId, page, search]);

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  const columns = [
    translate("customer-name"),
    translate("email"),
    translate("phone-number"),
    translate("actions"),
  ];

  return (
    <CampaignsMenu>
      {/* ── Top bar ── */}
      <div className="flex justify-between mb-4 items-center gap-4">
        <Link
          to="/campaigns/lists"
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          {translate("back-to-lists")}
        </Link>

        <div className="flex gap-8">
          <CampaignCustomerAddModal
            key={"addCustomer"}
            buttonName={"add-customer-list"}
            campaignListId={listId}
            getCustomerLists={getCustomers}
          />

          {/* Search */}
          <div>
            <label
              htmlFor="search"
              className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-200"
            >
              {translate("search")}
            </label>
            <input
              type="text"
              id="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`${translate("search")}...`}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg
                focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5
                dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400
                dark:focus:ring-blue-500 dark:focus:border-blue-500"
            />
          </div>

          {/* List meta */}
          <div className="flex flex-col">
            <label className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              {campaignListData?.name ?? translate("customer-list")}
            </label>
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {translate("total-customers")}: {totalCustomerLists}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Table card ── */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm ring-1 ring-gray-100 dark:ring-gray-700/60 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-blue-700 dark:bg-blue-800">
              {columns.map((col, i) => (
                <th
                  key={col}
                  className={`px-4 py-3.5 text-xs font-semibold text-blue-100 uppercase tracking-wider
                    ${i >= 2 && i <= 4 ? "text-center" : i >= 5 ? "text-center" : "text-left"}`}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="py-12">
                  <Loading />
                </td>
              </tr>
            ) : customersData?.length > 0 ? (
              customersData.map((list) => (
                <tr
                  key={list._id}
                  className="group relative hover:bg-blue-50/40 dark:hover:bg-blue-900/20 transition-colors duration-150"
                >
                  {/* Name */}
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className="w-0.5 h-5 rounded-full bg-blue-700 dark:bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity -ml-1" />
                      <span className="font-semibold text-gray-800 dark:text-gray-100 text-sm">
                        {list?.customer?.fullName}
                      </span>
                    </div>
                  </td>

                  {/* Email */}
                  <td className="px-4 py-3.5">
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {list?.customer?.email}
                    </span>
                  </td>

                  {/* Phone */}
                  <td className="px-4 py-3.5 text-center text-sm text-gray-700 dark:text-gray-300">
                    {list?.customer?.phoneNumber}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3.5 text-center">
                    <button
                      className="ml-2 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      onClick={() => {
                        setSelectedFile(list);
                        setIsDeleteModalOpen(true);
                      }}
                    >
                      {translate("remove")}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <EmptyState />
            )}
          </tbody>
        </table>

        {/* Footer with pagination */}
        {!isLoading && customersData?.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/40">
            <Pagination
              key="customersData"
              data={customersData}
              page={page}
              pageNumber={pageNumber}
              isLoading={isLoading}
              setPage={setPage}
              totalRecords={totalCustomerLists}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => handleRemoveCustomer()}
        fileName={selectedFile?.customer?.fullName || ""}
        title={translate("remove-customer-list")}
      />
    </CampaignsMenu>
  );
};

export default CampaignEditCustomersList;
